import { useEffect } from "react";
import Reveal from "@/components/Reveal";
import { Star } from "lucide-react";
import { siteConfig } from "@/lib/site-config";
import DeferUntilVisible from "@/components/DeferUntilVisible";

/** Reserved height for the embed, so mounting it shifts nothing. */
const REVIEW_EMBED_MIN_HEIGHT = 420;

/**
 * The iframe and its resizer script together pull in around a megabyte of
 * third-party code, so both are held back until the section nears the viewport.
 */
const ReviewEmbed = () => {
  useEffect(() => {
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <iframe
      className="lc_reviews_widget"
      src={siteConfig.widgets.reviewIframeSrc}
      scrolling="no"
      loading="lazy"
      style={{
        minWidth: "100%",
        width: "100%",
        minHeight: REVIEW_EMBED_MIN_HEIGHT,
        border: "none",
        display: "block",
      }}
      title="Customer reviews"
    />
  );
};

/**
 * Reputation Hub review widget — preserves original embed height,
 * no clipping, no scrollbars, no forced max-height.
 */
const ReviewWidget = () => {

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <Reveal
            as="span"
            y={10}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </Reveal>
          <Reveal
            as="h2"
            y={20}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </Reveal>
          <Reveal
            as="div"
            className="flex items-center justify-center gap-1 mb-3"
          >
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-quaternary text-quaternary" />
            ))}
          </Reveal>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Real reviews from real homeowners and businesses across the Phoenix Valley.
          </p>
        </div>

        <Reveal
          as="div"
          y={20}
          duration={0.5}
          className="review-widget-wrap max-w-5xl mx-auto"
        >
          <DeferUntilVisible minHeight={REVIEW_EMBED_MIN_HEIGHT}>
            <ReviewEmbed />
          </DeferUntilVisible>
        </Reveal>
      </div>
    </section>
  );
};

export default ReviewWidget;
